import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Library, ShieldCheck, ShieldAlert, Lock, Layers, Crosshair } from "lucide-react";

function Field({ label, value, cls }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm mono mt-0.5 ${cls || "text-slate-200"}`}>{value}</div>
    </div>
  );
}

/** One opportunity model per market. Both feed the SAME execution engine below. */
function OpportunityModel({ badge, badgeCls, title, subtitle, formulas, notes }) {
  return (
    <div className="term-panel-2 rounded-sm p-4 space-y-2" data-testid={`opportunity-model-${badge.toLowerCase()}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${badgeCls}`}>{badge} FUT</span>
        <span className="text-sm font-bold text-fg">{title}</span>
      </div>
      <div className="text-[11px] text-slate-500">{subtitle}</div>
      <div className="mono text-[10px] text-slate-300 space-y-0.5 pt-1 border-t border-[#2A2F3A]">
        {formulas.map((f) => <div key={f}>{f}</div>)}
      </div>
      {notes && <div className="text-[10px] text-slate-600">{notes}</div>}
    </div>
  );
}

export default function StrategyLibrary() {
  const [versions, setVersions] = useState(null);
  const [parity, setParity] = useState(null);

  useEffect(() => {
    api.get("/environments/strategies").then((r) => setVersions(r.data.strategy_versions || [])).catch(() => setVersions([]));
    api.get("/paper/v25/parity").then((r) => setParity(r.data)).catch(() => {});
  }, []);

  const v25 = (versions || []).find((v) => v.version === "excel_groundzero_v2_5");

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
        <Library className="h-5 w-5 text-[#2563EB]" /> Strategy Library
        <span className="text-xs font-normal ml-2 text-slate-500">read-only · verified production strategy</span>
      </h1>

      <div className="term-panel rounded-sm p-5 space-y-4" data-testid="strategy-card-v25">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-base font-bold text-fg">Excel V2.5 — Futures Calendar Spread Engine</div>
            <div className="text-[11px] text-slate-500 mono mt-0.5">
              Source workbook: “Analysi and Trade Hedging Far First V2.5.xlsm”
            </div>
          </div>
          <div className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-sm ${parity?.passed ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#FF6b62]/15 text-[#FF6b62]"}`}>
            {parity?.passed ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
            PARITY {parity ? (parity.passed ? "PASS" : "FAIL") : "…"}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2 border-t border-[#2A2F3A]">
          <Field label="Strategy ID" value="excel_groundzero_v2_5" />
          <Field label="Formula / Compatibility" value={v25?.formula_version || "hedging_far_first_v2_5"} />
          <Field label="Markets" value="MCX FUT · NFO FUT" />
          <Field label="Instrument" value="FUT (calendar spread)" />
          <Field label="Parity Checks" value={parity ? `${parity.checked_analysier} Analysier · ${parity.checked_dashboard} Dashboard` : "…"} />
          <Field label="Execution Provider" value="PAPER_BROKER" />
          <Field label="PAPER Readiness" value="READY" cls="text-[#00C805] font-semibold" />
          <Field label="LIVE Readiness" value="NOT APPROVED" cls="text-[#FF6b62] font-semibold" />
        </div>

        {parity && !parity.passed && (
          <div className="text-[11px] text-[#FF6b62] mono bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-2">
            {JSON.stringify(parity.failures)}
          </div>
        )}
      </div>

      {/* ---------------- opportunity models (per market) ---------------- */}
      <div className="space-y-2">
        <div className="text-xs font-bold tracking-tight flex items-center gap-2 text-slate-300">
          <Crosshair className="h-4 w-4 text-[#00C805]" /> Opportunity models — market-specific Analysier formulas
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <OpportunityModel
            badge="MCX" badgeCls="bg-[#FFCC00]/15 text-[#FFCC00]"
            title="Decay model"
            subtitle="Two legs (near / far futures). Decay factor from the Data_Misc day table."
            formulas={[
              "f = VLOOKUP(K, Data_Misc!P:Q)",
              "H = gap − |gap|·f    I = −gap + |gap|·f",
              "J = MAX(I,H)·G",
              "L = RefLTP·G·funds%   M = L·margin%",
              "N = J / M    O = N / K",
            ]}
            notes="Per-commodity funds%/margin% coefficients."
          />
          <OpportunityModel
            badge="NFO" badgeCls="bg-[#5b9bff]/15 text-[#5b9bff]"
            title="Linear spot-anchored model"
            subtitle="Three inputs (near / far futures + spot). Spot is a price input only and is never traded."
            formulas={[
              "H = (Bfar − Bspot)·K/50 + (Bspot − Bnear)",
              "I = −H",
              "J = MAX(I,H)·G",
              "L = Bspot·G·22.5%   M = L·20%",
              "N = J / M    O = N / K",
            ]}
            notes="Flat 22.5% / 20% constants across all 62 symbols."
          />
        </div>
      </div>

      {/* ---------------- shared execution engine ---------------- */}
      <div className="term-panel rounded-sm p-5 space-y-3" data-testid="execution-engine-card">
        <div className="text-sm font-bold text-fg flex items-center gap-2">
          <Layers className="h-4 w-4 text-[#5b9bff]" /> Shared Dashboard execution engine
          <span className="text-[10px] font-normal px-2 py-0.5 rounded-sm bg-[#1A1D24] text-slate-400">one engine · all markets</span>
        </div>
        <div className="text-[11px] text-slate-500">
          The workbook ranks MCX and NFO in one Sorter list (<span className="mono">Analysier!AA2:AN82</span>) and runs whichever
          opportunity is selected through the same Dashboard trade block. Once an opportunity is selected the market no longer matters.
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-[#2A2F3A]">
          <Field label="Ranking" value="%Profit (N) desc" />
          <Field label="Entry gate" value="K1 edge, every level" />
          <Field label="Level trigger" value="Z(n) ≥ AB(n)" />
          <Field label="Reference ladder" value="AA(n+1) = AA(n)·1.08" />
          <Field label="Lot progression" value="1, 2, 6, 13, 26 …" />
          <Field label="Order sequencing" value="Far First" />
          <Field label="Profit target" value="G24 (auto square-off)" />
          <Field label="Stop indicator" value="A25 (display only)" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-[#2A2F3A]">
          <Field label="Execution modes" value="EXCEL_V2_5_LITERAL · CUSTOM_CONFIGURED" />
          <Field label="Paper capital" value="₹50,00,000 per trade (independent)" />
          <Field label="Market data" value="ZERODHA (live)" />
          <Field label="Trade instances" value="Unlimited · no slots" />
        </div>
      </div>

      <div className="text-[10px] text-slate-600 flex items-center gap-1.5">
        <Lock className="h-3 w-3" /> Verified formulas are locked — edits are not exposed here. Lot size and days-to-expiry
        are taken live from the exchange, so values are formula-parity with the workbook, not identical to its cached cells.
      </div>
    </div>
  );
}
